import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE } from '../api/client';

interface HelpMessage {
  id: string;
  sender: 'guest' | 'organizer';
  senderName: string;
  body: string;
  createdAt: string;
}

interface HelpInquiry {
  id: string;
  code: string;
  guestName: string;
  guestPhone: string;
  topic: string | null;
  status: string;
  messages: HelpMessage[];
}

const PRIMARY = '#1a6b3c';
const TEXT_DARK = '#0f2d1f';
const TEXT_MUTED = '#6b7280';
const BORDER = '#e5e7eb';

const STORAGE_KEY = 'kdu_helpdesk_code';

export function GuestHelpDeskModal({ onClose }: { onClose: () => void }) {
  // Restore an in-progress session if the guest already has a code
  const savedCode = (() => {
    try { return localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
  })();

  const [stage, setStage] = useState<'form' | 'chat'>(savedCode ? 'chat' : 'form');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [inquiry, setInquiry] = useState<HelpInquiry | null>(null);
  const [code, setCode] = useState(savedCode);
  const [chatText, setChatText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Poll the thread while in chat
  const fetchThread = useCallback(async (theCode: string) => {
    try {
      const res = await fetch(`${API_BASE}/help-desk/inquiries/code/${encodeURIComponent(theCode)}`);
      if (res.status === 404) {
        // Stale code — clear and go back to the form
        try { localStorage.removeItem(STORAGE_KEY); } catch {}
        setStage('form');
        setCode('');
        return;
      }
      if (!res.ok) return;
      const data: HelpInquiry = await res.json();
      setInquiry(data);
    } catch {
      // Silent — will retry on next poll
    }
  }, []);

  useEffect(() => {
    if (stage !== 'chat' || !code) return;
    fetchThread(code);
    const timer = setInterval(() => fetchThread(code), 5000);
    return () => clearInterval(timer);
  }, [stage, code, fetchThread]);

  useEffect(() => {
    scrollToBottom();
  }, [inquiry?.messages.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError('Please enter your name and phone number.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/help-desk/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestName: name.trim(),
          guestPhone: phone.trim(),
          topic: topic.trim() || undefined,
          message: message.trim() || undefined,
        }),
      });
      if (!res.ok) {
        setError('Could not start a help session. Please try again.');
        return;
      }
      const data: HelpInquiry = await res.json();
      setInquiry(data);
      setCode(data.code);
      try { localStorage.setItem(STORAGE_KEY, data.code); } catch {}
      setStage('chat');
    } catch {
      setError('Unable to connect. Please check your internet and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatText.trim() || !code) return;
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/help-desk/inquiries/code/${encodeURIComponent(code)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: chatText.trim() }),
      });
      if (res.ok) {
        const data: HelpInquiry = await res.json();
        setInquiry(data);
        setChatText('');
      }
    } catch {
      // ignore, poll will catch up
    } finally {
      setSending(false);
    }
  };

  const handleEndSession = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label="Get live help">
      <div style={styles.sheet}>
        <button style={styles.closeBtn} onClick={onClose} aria-label="Close help">✕</button>

        {stage === 'form' && (
          <div style={styles.body}>
            <h2 style={styles.title}>🙋 Get Live Help</h2>
            <p style={styles.subtitle}>
              Having trouble logging in or finding your seat? Leave your name and number and a KDU helper will chat with you right here.
            </p>

            {error && <div style={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="hd-name">Your Name *</label>
                <input id="hd-name" style={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="First and last name" required />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="hd-phone">Phone Number *</label>
                <input id="hd-phone" style={styles.input} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(404) 555-0100" required />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="hd-topic">What do you need help with?</label>
                <select id="hd-topic" style={styles.input} value={topic} onChange={(e) => setTopic(e.target.value)}>
                  <option value="">— Select —</option>
                  <option value="Can't log in">Can't log in</option>
                  <option value="Print my seat card">Print my seat card</option>
                  <option value="Find my seat / table">Find my seat / table</option>
                  <option value="Forgot password">Forgot password</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="hd-msg">Message (optional)</label>
                <textarea id="hd-msg" style={styles.textarea} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us briefly what's happening..." />
              </div>
              <button type="submit" style={styles.primaryBtn} disabled={submitting}>
                {submitting ? 'Starting...' : 'Start Help Session'}
              </button>
            </form>
          </div>
        )}

        {stage === 'chat' && (
          <div style={styles.chatWrap}>
            <div style={styles.chatHeader}>
              <div>
                <div style={styles.title}>🙋 KDU Help Desk</div>
                {inquiry && (
                  <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '2px' }}>
                    Your code: <strong style={{ color: PRIMARY, letterSpacing: '1px' }}>{inquiry.code}</strong>
                    {' · '}
                    <span>{inquiry.status === 'Resolved' ? 'Resolved' : 'A helper will respond shortly'}</span>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.messages}>
              {!inquiry || inquiry.messages.length === 0 ? (
                <div style={styles.emptyChat}>
                  <p style={{ margin: 0 }}>You're connected. A KDU helper will reply here soon.</p>
                  <p style={{ margin: '8px 0 0', fontSize: '0.78rem' }}>Keep this window open. Type below to add more details.</p>
                </div>
              ) : (
                inquiry.messages.map((m) => (
                  <div key={m.id} style={{ display: 'flex', justifyContent: m.sender === 'guest' ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                    <div style={m.sender === 'guest' ? styles.bubbleGuest : styles.bubbleOrganizer}>
                      {m.sender === 'organizer' && <div style={styles.bubbleName}>{m.senderName}</div>}
                      <div>{m.body}</div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendChat} style={styles.chatInputRow}>
              <input
                style={styles.chatInput}
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                placeholder="Type a message..."
                aria-label="Message"
              />
              <button type="submit" style={styles.sendBtn} disabled={sending || !chatText.trim()}>
                {sending ? '...' : 'Send'}
              </button>
            </form>

            <button style={styles.endBtn} onClick={handleEndSession}>End session</button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 10000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
  },
  sheet: {
    backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  closeBtn: {
    position: 'absolute', top: '10px', right: '10px', width: '32px', height: '32px',
    borderRadius: '50%', background: 'rgba(0,0,0,0.06)', border: 'none', fontSize: '1rem',
    cursor: 'pointer', color: TEXT_MUTED, zIndex: 2,
  },
  body: { padding: '1.5rem', overflowY: 'auto' },
  title: { fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, margin: 0 },
  subtitle: { fontSize: '0.85rem', color: TEXT_MUTED, margin: '0.5rem 0 1.25rem', lineHeight: 1.5 },
  field: { marginBottom: '0.85rem' },
  label: { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.25rem' },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', border: `1px solid ${BORDER}`, borderRadius: '8px',
    fontSize: '16px', boxSizing: 'border-box', backgroundColor: '#fff',
  },
  textarea: {
    width: '100%', padding: '0.6rem 0.75rem', border: `1px solid ${BORDER}`, borderRadius: '8px',
    fontSize: '16px', boxSizing: 'border-box', minHeight: '70px', resize: 'vertical',
  },
  primaryBtn: {
    width: '100%', padding: '0.8rem', backgroundColor: PRIMARY, color: '#fff', border: 'none',
    borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', marginTop: '0.5rem',
  },
  error: {
    color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.6rem 0.75rem', borderRadius: '8px',
    marginBottom: '1rem', fontSize: '0.85rem',
  },
  chatWrap: { display: 'flex', flexDirection: 'column', height: '70vh', maxHeight: '600px' },
  chatHeader: { padding: '1.25rem 1.25rem 0.75rem', borderBottom: `1px solid ${BORDER}` },
  messages: { flex: 1, overflowY: 'auto', padding: '1rem 1.25rem', backgroundColor: '#f9fafb' },
  emptyChat: { textAlign: 'center', color: TEXT_MUTED, fontSize: '0.88rem', padding: '2rem 1rem' },
  bubbleGuest: {
    maxWidth: '78%', backgroundColor: PRIMARY, color: '#fff', padding: '0.55rem 0.8rem',
    borderRadius: '14px 14px 4px 14px', fontSize: '0.88rem', lineHeight: 1.4,
  },
  bubbleOrganizer: {
    maxWidth: '78%', backgroundColor: '#fff', color: TEXT_DARK, padding: '0.55rem 0.8rem',
    borderRadius: '14px 14px 14px 4px', fontSize: '0.88rem', lineHeight: 1.4,
    border: `1px solid ${BORDER}`,
  },
  bubbleName: { fontSize: '0.68rem', fontWeight: 700, color: PRIMARY, marginBottom: '2px' },
  chatInputRow: { display: 'flex', gap: '0.5rem', padding: '0.75rem 1.25rem', borderTop: `1px solid ${BORDER}` },
  chatInput: {
    flex: 1, padding: '0.6rem 0.75rem', border: `1px solid ${BORDER}`, borderRadius: '20px',
    fontSize: '16px', outline: 'none', boxSizing: 'border-box',
  },
  sendBtn: {
    padding: '0.6rem 1.1rem', backgroundColor: PRIMARY, color: '#fff', border: 'none',
    borderRadius: '20px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
  },
  endBtn: {
    background: 'none', border: 'none', color: TEXT_MUTED, fontSize: '0.78rem',
    padding: '0.5rem', cursor: 'pointer',
  },
};
